import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import { useHasHover } from '../Common/MobileTooltip';

// Add custom styles to remove outline and hide tooltips during drag
const customStyles = `
  .leaflet-interactive:focus {
    outline: none !important;
  }
  .leaflet-interactive {
    outline: none !important;
  }
  .leaflet-container a.leaflet-popup-close-button {
    outline: none !important;
  }
  .leaflet-container.is-dragging .leaflet-tooltip {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  @media (hover: hover) {
    .leaflet-container.is-dragging .leaflet-interactive {
      pointer-events: none !important;
    }
  }
  .leaflet-control-attribution {
    font-size: 0 !important;
  }
  .leaflet-control-attribution * {
    font-size: 11px !important;
  }
  .leaflet-control-attribution img,
  .leaflet-control-attribution svg {
    display: none !important;
  }
  .leaflet-control-attribution::before {
    content: '' !important;
    letter-spacing: 2px !important;
  }
  .leaflet-control-attribution {
    word-spacing: 3px !important;
  }
`;

const FlightsMap = ({ 
  isSharedView = false, 
  sharedPassengerId = null, 
  sharedFlightsType = null, 
  sharedUid = null,
  sharedMapData = null // Новый prop с готовыми данными
}) => {
  const [completedRoutes, setCompletedRoutes] = useState([]);
  const [futureRoutes, setFutureRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [flightType, setFlightType] = useState('completed'); // 'completed', 'future'
  const hasHover = useHasHover(); // true = has mouse (use tooltip), false = touch only (use popup)
  const isDraggingRef = useRef(false); // Track if map is being dragged
  const [mapHeight, setMapHeight] = useState('700px');
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const pageHeaderRef = useRef(null);
  const legendBlockRef = useRef(null);

  useEffect(() => {
    // Inject custom styles
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Detect mobile and calculate map height to fit viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    const calculateMapHeight = () => {
      // Get viewport height
      const viewportHeight = window.innerHeight;
      const isMobileView = window.innerWidth <= 768;
      
      // Get actual heights of elements using refs
      const headerHeight = pageHeaderRef.current ? pageHeaderRef.current.offsetHeight : 0;
      const legendHeight = legendBlockRef.current ? legendBlockRef.current.offsetHeight : 0;
      
      // Get computed margin-bottom of page-header (typically 30px from CSS)
      const headerMarginBottom = pageHeaderRef.current 
        ? parseInt(window.getComputedStyle(pageHeaderRef.current).marginBottom, 10) || 0
        : 0;
      
      // Get computed margin-bottom of legend block (typically 15px)
      const legendMarginBottom = legendBlockRef.current 
        ? parseInt(window.getComputedStyle(legendBlockRef.current).marginBottom, 10) || 0
        : 0;
      
      // Small buffer to account for rounding errors, borders, etc.
      // Negative buffer on mobile to fill the gap completely
      const safetyBuffer = isMobileView ? -1 : 5;
      
      if (isSharedView) {
        // Shared view layout
        // Navigation bar at top (sticky) - measure actual or use approximate
        const navHeight = isMobileView ? 40 : 52;
        
        // Content padding in SharePage (20px top + 15px bottom on desktop, 0 on mobile via CSS)
        const contentPadding = isMobileView ? 0 : 35;
        
        // Calculate available height for map
        const availableHeight = viewportHeight - navHeight - headerHeight - headerMarginBottom - legendHeight - legendMarginBottom - contentPadding - safetyBuffer;
        
        // Set minimum height of 300px
        const finalHeight = Math.max(availableHeight, 300);
        
        setMapHeight(`${finalHeight}px`);
      } else {
        // Normal view layout (inside main app with sidebar)
        // Main content padding (20px top + 20px bottom from .main-content)
        const containerPadding = 40;
        
        // Calculate available height for map
        const availableHeight = viewportHeight - headerHeight - headerMarginBottom - legendHeight - legendMarginBottom - containerPadding - safetyBuffer;
        
        // Set minimum height of 300px for normal view
        const finalHeight = Math.max(availableHeight, 300);
        
        setMapHeight(`${finalHeight}px`);
      }
    };
    
    checkMobile();
    
    // Use setTimeout to allow DOM to render before measuring
    const timeoutId = setTimeout(calculateMapHeight, 0);
    
    window.addEventListener('resize', checkMobile);
    window.addEventListener('resize', calculateMapHeight);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('resize', calculateMapHeight);
    };
  }, [isSharedView, loading, completedRoutes.length, futureRoutes.length, flightType]); // Re-calculate when loading finishes or routes change

  useEffect(() => {
    // Load passengers list and set default
    const fetchPassengers = async () => {
      if (isSharedView) {
        // In shared view, use passenger from share link
        setSelectedPassenger(sharedPassengerId);
        return;
      }
      
      try {
        const response = await api.get('/passengers');
        setPassengers(response.data);
        
        // Set default passenger (passenger_is_def = 1)
        const defaultPassenger = response.data.find(p => p.passenger_is_def === 1);
        if (defaultPassenger) {
          setSelectedPassenger(defaultPassenger.passenger_id);
        } else if (response.data.length > 0) {
          // If no default, select first passenger
          setSelectedPassenger(response.data[0].passenger_id);
        }
      } catch (err) {
        console.error('Failed to load passengers:', err);
      }
    };
    fetchPassengers();
  }, [isSharedView, sharedPassengerId]);

  useEffect(() => {
    const fetchData = async () => {
      // Если есть готовые данные (shared view), используем их
      if (isSharedView && sharedMapData) {
        // Filter based on shared flights type
        if (sharedFlightsType === 1) {
          // Only past flights
          setCompletedRoutes(sharedMapData.completed || []);
          setFutureRoutes([]);
          setFlightType('completed');
        } else if (sharedFlightsType === 2) {
          // Only future flights
          setCompletedRoutes([]);
          setFutureRoutes(sharedMapData.future || []);
          setFlightType('future');
        } else {
          // All flights
          setCompletedRoutes(sharedMapData.completed || []);
          setFutureRoutes(sharedMapData.future || []);
        }
        setLoading(false);
        return;
      }
      
      // Иначе загружаем данные (обычный режим)
      if (!selectedPassenger) return;
      
      try {
        setLoading(true);
        
        const params = { passenger_id: selectedPassenger };
        const response = await api.get('/flights-stat/map', { params });
        
        setCompletedRoutes(response.data.completed);
        setFutureRoutes(response.data.future);
        setError(null);
      } catch (err) {
        console.error('Failed to load flight routes:', err);
        setError('Failed to load flight routes. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPassenger, isSharedView, sharedUid, sharedFlightsType, sharedMapData]);

  // Calculate great circle path using proper spherical geometry
  const getGreatCirclePath = (lat1, lng1, lat2, lng2) => {
    const toRadians = (deg) => (deg * Math.PI) / 180;
    const toDegrees = (rad) => (rad * 180) / Math.PI;

    // Normalize longitudes to -180 to 180
    const normalizeLng = (lng) => {
      while (lng > 180) lng -= 360;
      while (lng < -180) lng += 360;
      return lng;
    };

    lng1 = normalizeLng(lng1);
    lng2 = normalizeLng(lng2);

    // Check if route crosses antimeridian and should wrap
    const lngDiff = lng2 - lng1;
    const crossesAntimeridian = Math.abs(lngDiff) > 180;
    
    // If crossing antimeridian, adjust for shorter path
    if (crossesAntimeridian) {
      if (lngDiff > 0) {
        lng1 += 360;
      } else {
        lng2 += 360;
      }
    }

    // Convert to radians
    const φ1 = toRadians(lat1);
    const λ1 = toRadians(lng1);
    const φ2 = toRadians(lat2);
    const λ2 = toRadians(lng2);

    // Calculate angular distance
    const Δλ = λ2 - λ1;
    const Δσ = Math.acos(
      Math.max(-1, Math.min(1, 
        Math.sin(φ1) * Math.sin(φ2) + 
        Math.cos(φ1) * Math.cos(φ2) * Math.cos(Δλ)
      ))
    );

    // If points are very close, return simple path
    if (Δσ < 0.001) {
      return { paths: [[[lat1, normalizeLng(lng1)], [lat2, normalizeLng(lng2)]]], crossesAntimeridian: false };
    }

    const path = [];
    const numPoints = 100;

    for (let i = 0; i <= numPoints; i++) {
      const fraction = i / numPoints;

      // Calculate intermediate point using Slerp formula
      const a = Math.sin((1 - fraction) * Δσ) / Math.sin(Δσ);
      const b = Math.sin(fraction * Δσ) / Math.sin(Δσ);

      const x = a * Math.cos(φ1) * Math.cos(λ1) + b * Math.cos(φ2) * Math.cos(λ2);
      const y = a * Math.cos(φ1) * Math.sin(λ1) + b * Math.cos(φ2) * Math.sin(λ2);
      const z = a * Math.sin(φ1) + b * Math.sin(φ2);

      const φi = Math.atan2(z, Math.sqrt(x * x + y * y));
      const λi = Math.atan2(y, x);

      path.push([toDegrees(φi), toDegrees(λi)]);
    }

    // If crosses antimeridian, split into two paths
    if (crossesAntimeridian) {
      const paths = [[], []];
      let currentPath = 0;
      
      for (let i = 0; i < path.length - 1; i++) {
        const [lat, lng] = path[i];
        const [nextLat, nextLng] = path[i + 1];
        
        paths[currentPath].push([lat, normalizeLng(lng)]);
        
        // Check if we cross the antimeridian between this point and next
        if (Math.abs(nextLng - lng) > 180) {
          currentPath = 1;
          paths[currentPath].push([nextLat, normalizeLng(nextLng)]);
        }
      }
      
      // Add last point
      const [lastLat, lastLng] = path[path.length - 1];
      paths[currentPath].push([lastLat, normalizeLng(lastLng)]);
      
      return { paths: paths.filter(p => p.length > 1), crossesAntimeridian: true };
    }

    return { paths: [path.map(([lat, lng]) => [lat, normalizeLng(lng)])], crossesAntimeridian: false };
  };

  // Select routes based on flight type
  const routes = flightType === 'completed' ? completedRoutes : futureRoutes;

  // Calculate max flight count for dynamic thresholds
  const maxFlightCount = Math.max(...routes.map(r => r.flight_count), 1);
  const totalFlights = routes.reduce((sum, r) => sum + r.flight_count, 0);

  // Calculate Natural Breaks thresholds for completed routes
  const calculateNaturalBreaks = (data, numClasses) => {
    if (data.length === 0) return [];
    
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue;
    
    if (range === 0) return [];
    
    const intervalSize = range / numClasses;
    const breaks = [];
    
    // Calculate equal interval breaks
    for (let i = 1; i < numClasses; i++) {
      breaks.push(minValue + (intervalSize * i));
    }
    
    return breaks;
  };

  // Calculate thresholds for completed flights only
  const completedPercentages = flightType === 'completed' 
    ? completedRoutes.map(r => (r.flight_count / completedRoutes.reduce((sum, route) => sum + route.flight_count, 0)) * 100)
    : [];
  const thresholds = flightType === 'completed' && completedRoutes.length > 0
    ? calculateNaturalBreaks(completedPercentages, 6)
    : [];

  // Calculate color based on percentage of total flights
  const getColorForCount = (count) => {
    // For future flights, use single color
    if (flightType === 'future') return '#1E90FF'; // DodgerBlue for all future flights
    
    const percentage = (count / totalFlights) * 100;
    
    // Use dynamic thresholds if available
    if (thresholds.length >= 5) {
      if (percentage >= thresholds[4]) return '#8B0000';  // Dark red - very frequent
      if (percentage >= thresholds[3]) return '#CC0000';   // Red - above average
      if (percentage >= thresholds[2]) return '#FF4500';   // Orange-red - average
      if (percentage >= thresholds[1]) return '#FF6347';   // Tomato - below average
      if (percentage >= thresholds[0]) return '#FF8C00';   // Dark orange - low
      return '#228B22';                                    // Forest green - rare
    }
    
    // Fallback to fixed thresholds
    if (percentage >= 10) return '#8B0000';
    if (percentage >= 8) return '#CC0000';
    if (percentage >= 6) return '#FF4500';
    if (percentage >= 4) return '#FF6347';
    if (percentage >= 2) return '#FF8C00';
    return '#228B22';
  };

  // Get color category name for legend
  const getColorCategory = (percentage) => {
    // Use dynamic thresholds if available
    if (thresholds.length >= 5) {
      if (percentage >= thresholds[4]) return `Very frequent (≥${thresholds[4].toFixed(2)}%)`;
      if (percentage >= thresholds[3]) return `Above average (${thresholds[3].toFixed(2)}-${thresholds[4].toFixed(2)}%)`;
      if (percentage >= thresholds[2]) return `Average (${thresholds[2].toFixed(2)}-${thresholds[3].toFixed(2)}%)`;
      if (percentage >= thresholds[1]) return `Below average (${thresholds[1].toFixed(2)}-${thresholds[2].toFixed(2)}%)`;
      if (percentage >= thresholds[0]) return `Low (${thresholds[0].toFixed(2)}-${thresholds[1].toFixed(2)}%)`;
      return `Rare (<${thresholds[0].toFixed(2)}%)`;
    }
    
    // Fallback to fixed categories
    if (percentage >= 10) return 'Very frequent (≥10%)';
    if (percentage >= 8) return 'Above average (8-10%)';
    if (percentage >= 6) return 'Average (6-8%)';
    if (percentage >= 4) return 'Below average (4-6%)';
    if (percentage >= 2) return 'Low (2-4%)';
    return 'Rare (<2%)';
  };

  // Calculate line weight based on percentage
  const getWeightForCount = (count) => {
    // For future flights, use single weight
    if (flightType === 'future') return 2.5;
    
    const percentage = (count / totalFlights) * 100;
    
    if (percentage >= 10) return 3.5;
    if (percentage >= 8) return 3.25;
    if (percentage >= 6) return 3;
    if (percentage >= 4) return 2.5;
    if (percentage >= 2) return 2;
    return 1.5;
  };

  // Get unique airports for markers
  const getUniqueAirports = () => {
    const airportsMap = new Map();
    
    routes.forEach(route => {
      if (!airportsMap.has(route.from_iata)) {
        airportsMap.set(route.from_iata, {
          iata: route.from_iata,
          lat: parseFloat(route.from_lat),
          lng: parseFloat(route.from_lng),
          flights: 0
        });
      }
      if (!airportsMap.has(route.to_iata)) {
        airportsMap.set(route.to_iata, {
          iata: route.to_iata,
          lat: parseFloat(route.to_lat),
          lng: parseFloat(route.to_lng),
          flights: 0
        });
      }
      
      // Count flights for each airport
      const fromAirport = airportsMap.get(route.from_iata);
      const toAirport = airportsMap.get(route.to_iata);
      fromAirport.flights += route.flight_count;
      toAirport.flights += route.flight_count;
    });

    return Array.from(airportsMap.values());
  };

  const airports = routes.length > 0 ? getUniqueAirports() : [];

  // Calculate map center based on most-used airport
  const getMapCenter = () => {
    if (airports.length === 0) {
      return { center: [20, 0], zoom: 2 };
    }
    
    // Find airport with most flights
    const topAirport = airports.reduce((max, airport) => 
      airport.flights > max.flights ? airport : max, 
      airports[0]
    );
    
    // Offset latitude to account for partial map visibility
    // (map is 700px tall but upper portion is visible without scrolling)
    const latitudeOffset = 8; // degrees to shift center down so top airport appears in visible area
    
    return { 
      center: [topAirport.lat - latitudeOffset, topAirport.lng], 
      zoom: 5 
    };
  };

  const mapConfig = getMapCenter();

  // Component to recenter map when flight type changes
  const MapRecenterHandler = () => {
    const map = useMapEvents({});
    
    useEffect(() => {
      if (airports.length > 0) {
        const newConfig = getMapCenter();
        map.setView(newConfig.center, newConfig.zoom, { animate: true });
      }
    }, [flightType, map]);
    
    return null;
  };

  // Component to handle map clicks to close tooltips and popups
  const MapClickHandler = () => {
    const map = useMapEvents({
      click: () => {
        // Close all tooltips and popups
        map.eachLayer((layer) => {
          if (layer.getTooltip && layer.getTooltip()) {
            layer.closeTooltip();
          }
          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      },
      mousedown: () => {
        // Mark as dragging, disable pointer events, and close all popups
        isDraggingRef.current = true;
        map.getContainer().classList.add('is-dragging');
        map.eachLayer((layer) => {
          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      },
      mouseup: () => {
        // Re-enable after delay
        setTimeout(() => {
          isDraggingRef.current = false;
          map.getContainer().classList.remove('is-dragging');
        }, 100);
      },
      dragstart: () => {
        // Mark as dragging and close all popups
        isDraggingRef.current = true;
        map.getContainer().classList.add('is-dragging');
        map.eachLayer((layer) => {
          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      },
      dragend: () => {
        // Close tooltips and popups, then re-enable after delay
        setTimeout(() => {
          map.eachLayer((layer) => {
            if (layer.getTooltip && layer.getTooltip()) {
              layer.closeTooltip();
            }
            if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
              layer.closePopup();
            }
          });
          isDraggingRef.current = false;
          map.getContainer().classList.remove('is-dragging');
        }, 100);
      },
      movestart: () => {
        // Mark as dragging and close all popups
        isDraggingRef.current = true;
        map.getContainer().classList.add('is-dragging');
        map.eachLayer((layer) => {
          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      },
      moveend: () => {
        // Close tooltips and popups, then re-enable after delay
        setTimeout(() => {
          map.eachLayer((layer) => {
            if (layer.getTooltip && layer.getTooltip()) {
              layer.closeTooltip();
            }
            if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
              layer.closePopup();
            }
          });
          isDraggingRef.current = false;
          map.getContainer().classList.remove('is-dragging');
        }, 100);
      },
      zoomstart: () => {
        // Mark as dragging and close all popups
        isDraggingRef.current = true;
        map.getContainer().classList.add('is-dragging');
        map.eachLayer((layer) => {
          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      },
      zoomend: () => {
        // Close tooltips and popups, then re-enable after delay
        setTimeout(() => {
          map.eachLayer((layer) => {
            if (layer.getTooltip && layer.getTooltip()) {
              layer.closeTooltip();
            }
            if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
              layer.closePopup();
            }
          });
          isDraggingRef.current = false;
          map.getContainer().classList.remove('is-dragging');
        }, 100);
      }
    });
    return null;
  };

  return (
    <div className="page-container" style={isSharedView ? { maxWidth: '1300px', margin: '0 auto' } : {}}>
      {!isSharedView && (
        <div ref={pageHeaderRef} className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>Flights Map</h2>
          
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div>
              <label style={{ marginRight: '8px' }}>Passenger:</label>
              <select 
                value={selectedPassenger || ''} 
                onChange={(e) => setSelectedPassenger(parseInt(e.target.value, 10))}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                {passengers.map(p => (
                  <option key={p.passenger_id} value={p.passenger_id}>
                    {p.passenger_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, marginRight: '5px' }}>Flights Type:</span>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  value="completed" 
                  checked={flightType === 'completed'}
                  onChange={(e) => setFlightType(e.target.value)}
                  style={{ marginRight: '4px' }}
                />
                Completed
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  value="future" 
                  checked={flightType === 'future'}
                  onChange={(e) => setFlightType(e.target.value)}
                  style={{ marginRight: '4px' }}
                />
                Scheduled
              </label>
            </div>
          </div>
        </div>
      )}
      
      {isSharedView && sharedFlightsType !== 9 && (
        <div ref={pageHeaderRef} className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>Flights Map</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, marginRight: '5px' }}>Flights Type:</span>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input 
                type="radio" 
                value="completed" 
                checked={flightType === 'completed'}
                onChange={(e) => setFlightType(e.target.value)}
                style={{ marginRight: '4px' }}
                disabled={sharedFlightsType === 2}
              />
              Completed
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input 
                type="radio" 
                value="future" 
                checked={flightType === 'future'}
                onChange={(e) => setFlightType(e.target.value)}
                style={{ marginRight: '4px' }}
                disabled={sharedFlightsType === 1}
              />
              Scheduled
            </label>
          </div>
        </div>
      )}
      
      {isSharedView && sharedFlightsType === 9 && (
        <div ref={pageHeaderRef} className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>Flights Map</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, marginRight: '5px' }}>Flights Type:</span>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input 
                type="radio" 
                value="completed" 
                checked={flightType === 'completed'}
                onChange={(e) => setFlightType(e.target.value)}
                style={{ marginRight: '4px' }}
              />
              Completed
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input 
                type="radio" 
                value="future" 
                checked={flightType === 'future'}
                onChange={(e) => setFlightType(e.target.value)}
                style={{ marginRight: '4px' }}
              />
              Scheduled
            </label>
          </div>
        </div>
      )}

      {routes.length > 0 && (
        <div ref={legendBlockRef} className="map-stats-block" style={{ marginBottom: '15px', padding: '15px', background: 'white', borderRadius: '8px' }}>
          {/* Hide Color Legend on mobile in shared view */}
          <div style={{ 
            display: (isSharedView && isMobile) ? 'none' : 'flex', 
            flexWrap: 'wrap', 
            gap: '15px', 
            alignItems: 'center' 
          }}>
            <div style={{ fontWeight: 'bold' }}>Color Legend:</div>
            {flightType === 'completed' ? (
              thresholds.length >= 5 ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#228B22' }}></div>
                    <span style={{ fontSize: '12px' }}>Rare (&lt;{thresholds[0].toFixed(2)}%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF8C00' }}></div>
                    <span style={{ fontSize: '12px' }}>Low ({thresholds[0].toFixed(2)}-{thresholds[1].toFixed(2)}%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF6347' }}></div>
                    <span style={{ fontSize: '12px' }}>Below avg ({thresholds[1].toFixed(2)}-{thresholds[2].toFixed(2)}%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF4500' }}></div>
                    <span style={{ fontSize: '12px' }}>Average ({thresholds[2].toFixed(2)}-{thresholds[3].toFixed(2)}%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#CC0000' }}></div>
                    <span style={{ fontSize: '12px' }}>Above avg ({thresholds[3].toFixed(2)}-{thresholds[4].toFixed(2)}%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#8B0000' }}></div>
                    <span style={{ fontSize: '12px' }}>Very frequent (≥{thresholds[4].toFixed(2)}%)</span>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#228B22' }}></div>
                    <span style={{ fontSize: '12px' }}>Rare (&lt;2%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF8C00' }}></div>
                    <span style={{ fontSize: '12px' }}>Low (2-4%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF6347' }}></div>
                    <span style={{ fontSize: '12px' }}>Below avg (4-6%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#FF4500' }}></div>
                    <span style={{ fontSize: '12px' }}>Average (6-8%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#CC0000' }}></div>
                    <span style={{ fontSize: '12px' }}>Above avg (8-10%)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px', height: '3px', background: '#8B0000' }}></div>
                    <span style={{ fontSize: '12px' }}>Very frequent (≥10%)</span>
                  </div>
                </>
              )
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '30px', height: '3px', background: '#1E90FF' }}></div>
                <span style={{ fontSize: '12px' }}>Scheduled flights</span>
              </div>
            )}
          </div>
          <div style={{ 
            marginTop: (isSharedView && isMobile) ? '0' : '10px', 
            fontSize: '12px', 
            color: '#666',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px 16px',
            justifyContent: 'flex-start'
          }}>
            <span style={{ whiteSpace: 'nowrap' }}>Total routes: <strong>{routes.length}</strong></span>
            <span style={{ whiteSpace: 'nowrap' }}>Total airports: <strong>{airports.length}</strong></span>
            <span style={{ whiteSpace: 'nowrap' }}>Total flights: <strong>{totalFlights}</strong></span>
            {flightType === 'completed' && (
              <span style={{ whiteSpace: 'nowrap' }}>Max on single route: <strong>{maxFlightCount}</strong></span>
            )}
          </div>
        </div>
      )}

      {routes.length === 0 && (
        <div style={{ marginBottom: '15px', padding: '15px', background: 'white', borderRadius: '8px', textAlign: 'center', color: '#666' }}>
          No flight data available for the selected filters.
        </div>
      )}

      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', background: 'white', borderRadius: '8px' }}>
          Loading map data...
        </div>
      ) : error ? (
        <div style={{ padding: '20px', color: 'red', background: 'white', borderRadius: '8px' }}>
          {error}
        </div>
      ) : (
        <div ref={containerRef} style={{ height: mapHeight, background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
          <MapContainer
            center={mapConfig.center}
            zoom={mapConfig.zoom}
            style={{ height: '100%', width: '100%' }}
            minZoom={2}
            maxZoom={10}
            maxBounds={[[-90, -180], [90, 180]]}
            scrollWheelZoom={true}
          >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapRecenterHandler />
          <MapClickHandler />
          
          {/* Draw completed flight routes */}
          {flightType === 'completed' && completedRoutes.map((route, index) => {
            const lat1 = parseFloat(route.from_lat);
            const lng1 = parseFloat(route.from_lng);
            const lat2 = parseFloat(route.to_lat);
            const lng2 = parseFloat(route.to_lng);
            
            const { paths } = getGreatCirclePath(lat1, lng1, lat2, lng2);
            
            const completedTotal = completedRoutes.reduce((sum, r) => sum + r.flight_count, 0);
            const percentage = (route.flight_count / completedTotal) * 100;
            const color = getColorForCount(route.flight_count);
            const weight = getWeightForCount(route.flight_count);
            const bufferWeight = 15;
            
            const tooltipContent = (
              <div>
                <strong>{route.from_iata} ⇄ {route.to_iata}</strong><br/>
                Flights: {route.flight_count} ({percentage.toFixed(2)}% of total)<br/>
                Category: {getColorCategory(percentage)}
              </div>
            );
            
            return paths.map((positions, pathIndex) => (
              <React.Fragment key={`completed-${index}-${pathIndex}`}>
                <Polyline
                  positions={positions}
                  color="transparent"
                  weight={bufferWeight}
                  opacity={0}
                  interactive={true}
                  eventHandlers={{
                    mouseover: (e) => {
                      if (hasHover && e.target._map) {
                        // Close all popups when hovering (to show tooltip)
                        e.target._map.eachLayer((layer) => {
                          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
                            layer.closePopup();
                          }
                        });
                      }
                    }
                  }}
                >
                  {!hasHover && (
                    <Popup closeButton={false}>
                      {tooltipContent}
                    </Popup>
                  )}
                  {hasHover && (
                    <Tooltip sticky direction="top" offset={[0, -10]}>
                      {tooltipContent}
                    </Tooltip>
                  )}
                </Polyline>
                
                <Polyline
                  positions={positions}
                  color={color}
                  weight={weight}
                  opacity={0.8}
                  interactive={false}
                />
              </React.Fragment>
            ));
          })}
          
          {/* Draw future flight routes */}
          {flightType === 'future' && futureRoutes.map((route, index) => {
            const lat1 = parseFloat(route.from_lat);
            const lng1 = parseFloat(route.from_lng);
            const lat2 = parseFloat(route.to_lat);
            const lng2 = parseFloat(route.to_lng);
            
            const { paths } = getGreatCirclePath(lat1, lng1, lat2, lng2);
            
            const color = '#1E90FF';
            const weight = 2.5;
            const bufferWeight = 15;
            
            const tooltipContent = (
              <div>
                <strong>{route.from_iata} ⇄ {route.to_iata}</strong><br/>
                Flights: {route.flight_count}
              </div>
            );
            
            return paths.map((positions, pathIndex) => (
              <React.Fragment key={`future-${index}-${pathIndex}`}>
                <Polyline
                  positions={positions}
                  color="transparent"
                  weight={bufferWeight}
                  opacity={0}
                  interactive={true}
                  eventHandlers={{
                    mouseover: (e) => {
                      if (hasHover && e.target._map) {
                        // Close all popups when hovering (to show tooltip)
                        e.target._map.eachLayer((layer) => {
                          if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
                            layer.closePopup();
                          }
                        });
                      }
                    }
                  }}
                >
                  {!hasHover && (
                    <Popup closeButton={false}>
                      {tooltipContent}
                    </Popup>
                  )}
                  {hasHover && (
                    <Tooltip sticky direction="top" offset={[0, -10]}>
                      {tooltipContent}
                    </Tooltip>
                  )}
                </Polyline>
                
                <Polyline
                  positions={positions}
                  color={color}
                  weight={weight}
                  opacity={0.8}
                  interactive={false}
                />
              </React.Fragment>
            ));
          })}

          {/* Draw airport markers */}
          {airports.map((airport, index) => (
            <CircleMarker
              key={index}
              center={[airport.lat, airport.lng]}
              radius={Math.min(3 + Math.log(airport.flights + 1) * 1.5, 10)}
              fillColor="#0066cc"
              color="#003366"
              weight={1}
              opacity={0.8}
              fillOpacity={0.6}
              eventHandlers={{
                mouseover: (e) => {
                  if (hasHover && e.target._map) {
                    // Close all popups when hovering (to show tooltip)
                    e.target._map.eachLayer((layer) => {
                      if (layer.getPopup && layer.getPopup() && layer.isPopupOpen()) {
                        layer.closePopup();
                      }
                    });
                  }
                }
              }}
            >
              {!hasHover && (
                <Popup closeButton={false}>
                  <div>
                    <strong>{airport.iata}</strong><br/>
                    Total flights: {airport.flights}
                  </div>
                </Popup>
              )}
              {hasHover && (
                <Tooltip sticky direction="top" offset={[0, -10]}>
                  <div>
                    <strong>{airport.iata}</strong><br/>
                    Total flights: {airport.flights}
                  </div>
                </Tooltip>
              )}
            </CircleMarker>
          ))}
        </MapContainer>
        </div>
      )}
    </div>
  );
};

export default FlightsMap;
